import { HighlightArea, Wall, WallWithTempId, Point, Room, WallSection } from '../types';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';

/**
 * Converts pixels to meters using the standard conversion ratio
 */
function pixelsToMeters(pixels: number): number {
  return Math.round((pixels / PIXELS_PER_METER) * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculates the length of a wall segment
 */
function calculateWallLength(start: Point, end: Point): number {
  const pixelLength = Math.hypot(end.x - start.x, end.y - start.y);
  return pixelsToMeters(pixelLength);
}

/**
 * Checks if a wall intersects with a rectangular highlight area
 */
function doesWallIntersectHighlight(wall: Wall | WallWithTempId, highlight: HighlightArea): boolean {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  // Check if wall endpoints are within the highlight area
  const startInside = (wall.start.x >= minX && wall.start.x <= maxX && 
                      wall.start.y >= minY && wall.start.y <= maxY);
  const endInside = (wall.end.x >= minX && wall.end.x <= maxX && 
                    wall.end.y >= minY && wall.end.y <= maxY);

  if (startInside || endInside) {
    return true;
  }

  // Check if the wall line intersects with any of the highlight rectangle edges
  const wallStart = wall.start;
  const wallEnd = wall.end;
  
  // Define rectangle edges
  const rectEdges = [
    { start: { x: minX, y: minY }, end: { x: maxX, y: minY } }, // top
    { start: { x: maxX, y: minY }, end: { x: maxX, y: maxY } }, // right
    { start: { x: maxX, y: maxY }, end: { x: minX, y: maxY } }, // bottom
    { start: { x: minX, y: maxY }, end: { x: minX, y: minY } }  // left
  ];

  // Check intersection with each edge
  for (const edge of rectEdges) {
    if (doLinesIntersect(wallStart, wallEnd, edge.start, edge.end)) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if two line segments intersect
 */
function doLinesIntersect(p1: Point, q1: Point, p2: Point, q2: Point): boolean {
  function orientation(p: Point, q: Point, r: Point): number {
    const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    if (val === 0) return 0;  // collinear
    return (val > 0) ? 1 : 2; // clockwise or counterclockwise
  }

  function onSegment(p: Point, q: Point, r: Point): boolean {
    return q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
           q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
  }

  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  // General case
  if (o1 !== o2 && o3 !== o4) return true;

  // Special cases for collinear points
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;

  return false;
}

/**
 * Checks if a room intersects with a rectangular highlight area
 */
function doesRoomIntersectHighlight(room: Room, highlight: HighlightArea): boolean {
  if (!room.walls || room.walls.length === 0) return false;
  
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  // Check if any wall of the room intersects with the highlight area
  for (const wall of room.walls) {
    // Check if wall endpoints are within the highlight area
    const startInside = (wall.start.x >= minX && wall.start.x <= maxX && 
                        wall.start.y >= minY && wall.start.y <= maxY);
    const endInside = (wall.end.x >= minX && wall.end.x <= maxX && 
                      wall.end.y >= minY && wall.end.y <= maxY);

    if (startInside || endInside) {
      return true;
    }

    // Check if the wall line intersects with any of the highlight rectangle edges
    const wallStart = wall.start;
    const wallEnd = wall.end;
    
    // Define rectangle edges
    const rectEdges = [
      { start: { x: minX, y: minY }, end: { x: maxX, y: minY } }, // top
      { start: { x: maxX, y: minY }, end: { x: maxX, y: maxY } }, // right
      { start: { x: maxX, y: maxY }, end: { x: minX, y: maxY } }, // bottom
      { start: { x: minX, y: maxY }, end: { x: minX, y: minY } }  // left
    ];

    // Check intersection with each edge
    for (const edge of rectEdges) {
      if (doLinesIntersect(wallStart, wallEnd, edge.start, edge.end)) {
        return true;
      }
    }
  }

  // Also check if room center is within the highlight area
  if (room.center) {
    const centerInside = (room.center.x >= minX && room.center.x <= maxX && 
                         room.center.y >= minY && room.center.y <= maxY);
    if (centerInside) {
      return true;
    }
  }

  return false;
}

/**
 * Calculates the area of a highlight rectangle in square meters (fallback method)
 */
function calculateHighlightArea(highlight: HighlightArea): number {
  const width = Math.abs(highlight.endPoint.x - highlight.startPoint.x);
  const height = Math.abs(highlight.endPoint.y - highlight.startPoint.y);
  const pixelArea = width * height;
  const meterArea = pixelsToMeters(Math.sqrt(pixelArea)) ** 2;
  // Ensure exactly 2 decimal places for area accuracy
  return Math.round(meterArea * 100) / 100;
}

/**
 * Calculates the portion of a wall that intersects with a highlight area
 */
function calculateWallIntersectionLength(wall: Wall | WallWithTempId, highlight: HighlightArea): number {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  // Clamp wall endpoints to the highlight area boundaries
  const clampedStart = {
    x: Math.max(minX, Math.min(maxX, wall.start.x)),
    y: Math.max(minY, Math.min(maxY, wall.start.y))
  };
  const clampedEnd = {
    x: Math.max(minX, Math.min(maxX, wall.end.x)),
    y: Math.max(minY, Math.min(maxY, wall.end.y))
  };

  // Calculate the intersection length
  const intersectionLength = Math.hypot(
    clampedEnd.x - clampedStart.x,
    clampedEnd.y - clampedStart.y
  );

  return pixelsToMeters(intersectionLength);
}

/**
 * Calculates how many walls of a room are within the highlight selection
 */
function calculateRoomWallsInSelection(room: Room, highlight: HighlightArea): {
  wallsInSelection: number;
  totalWalls: number;
  wallsWithinPercentage: number;
} {
  if (!room.walls || room.walls.length === 0) {
    return { wallsInSelection: 0, totalWalls: 0, wallsWithinPercentage: 0 };
  }

  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  let wallsInSelection = 0;
  const totalWalls = room.walls.length;

  for (const wall of room.walls) {
    // Check if the majority of the wall is within the highlight area
    const startInside = (wall.start.x >= minX && wall.start.x <= maxX && 
                        wall.start.y >= minY && wall.start.y <= maxY);
    const endInside = (wall.end.x >= minX && wall.end.x <= maxX && 
                      wall.end.y >= minY && wall.end.y <= maxY);

    // Count wall as "in selection" if both endpoints are inside, or if wall center is inside
    if (startInside && endInside) {
      wallsInSelection++;
    } else if (startInside || endInside) {
      // If only one endpoint is inside, check if the wall center is inside
      const centerX = (wall.start.x + wall.end.x) / 2;
      const centerY = (wall.start.y + wall.end.y) / 2;
      const centerInside = (centerX >= minX && centerX <= maxX && 
                           centerY >= minY && centerY <= maxY);
      if (centerInside) {
        wallsInSelection++;
      }
    }
  }

  const wallsWithinPercentage = totalWalls > 0 ? (wallsInSelection / totalWalls) * 100 : 0;

  return {
    wallsInSelection,
    totalWalls,
    wallsWithinPercentage
  };
}

/**
 * Determines if a room should be included in the highlight calculation
 * based on how many of its walls are within the selection
 */
function shouldIncludeRoomInCalculation(room: Room, highlight: HighlightArea): boolean {
  const wallAnalysis = calculateRoomWallsInSelection(room, highlight);
  
  // Include room if:
  // 1. More than 50% of its walls are within the selection, OR
  // 2. At least 2 walls are within the selection (for rooms with 3-4 walls), OR
  // 3. All walls are within the selection (for small rooms)
  const includeRoom = wallAnalysis.wallsWithinPercentage > 50 || 
                     wallAnalysis.wallsInSelection >= 2 || 
                     wallAnalysis.wallsInSelection === wallAnalysis.totalWalls;

  console.log(`🎯 Room inclusion analysis for "${room.name}":`, {
    wallsInSelection: wallAnalysis.wallsInSelection,
    totalWalls: wallAnalysis.totalWalls,
    percentage: wallAnalysis.wallsWithinPercentage.toFixed(1) + '%',
    includeRoom
  });

  return includeRoom;
}

/**
 * Converts a room's walls into a polygon of points
 * Now properly handles rooms with split walls
 */
function getRoomPolygon(room: Room): Point[] {
  if (!room.walls || room.walls.length === 0) return [];
  
  const walls = [...room.walls]; // Copy to avoid modifying original
  console.log(`🔷 ROOM POLYGON for "${room.name}": ${walls.length} walls`);
  
  // Build an ordered chain of connected walls
  const orderedWalls: typeof walls = [];
  const usedWalls = new Set<number>();
  
  // Start with the first wall
  orderedWalls.push(walls[0]);
  usedWalls.add(0);
  
  let currentEnd = walls[0].end;
  const tolerance = 1;
  
  // Chain walls together by finding connected endpoints
  while (orderedWalls.length < walls.length) {
    let foundConnection = false;
    
    for (let i = 1; i < walls.length; i++) {
      if (usedWalls.has(i)) continue;
      
      const wall = walls[i];
      
      // Check if this wall's start connects to current end
      if (Math.abs(currentEnd.x - wall.start.x) < tolerance && 
          Math.abs(currentEnd.y - wall.start.y) < tolerance) {
        orderedWalls.push(wall);
        usedWalls.add(i);
        currentEnd = wall.end;
        foundConnection = true;
        break;
      }
      // Check if this wall's end connects to current end (reversed)
      else if (Math.abs(currentEnd.x - wall.end.x) < tolerance && 
               Math.abs(currentEnd.y - wall.end.y) < tolerance) {
        // Add wall in reverse
        orderedWalls.push({ ...wall, start: wall.end, end: wall.start });
        usedWalls.add(i);
        currentEnd = wall.start;
        foundConnection = true;
        break;
      }
    }
    
    // If no connection found, we have a broken chain - use remaining walls as-is
    if (!foundConnection) {
      console.warn(`⚠️ Room "${room.name}": Broken wall chain at ${orderedWalls.length}/${walls.length} walls`);
      for (let i = 0; i < walls.length; i++) {
        if (!usedWalls.has(i)) {
          orderedWalls.push(walls[i]);
          usedWalls.add(i);
        }
      }
      break;
    }
  }
  
  // Extract unique points from ordered walls
  const points: Point[] = [];
  for (const wall of orderedWalls) {
    // Add start point if it's not already the last point
    if (points.length === 0 || 
        Math.abs(points[points.length - 1].x - wall.start.x) > tolerance ||
        Math.abs(points[points.length - 1].y - wall.start.y) > tolerance) {
      points.push(wall.start);
    }
    
    // Always add end point (unless it closes the polygon)
    if (points.length === 0 ||
        Math.abs(points[0].x - wall.end.x) > tolerance ||
        Math.abs(points[0].y - wall.end.y) > tolerance) {
      points.push(wall.end);
    }
  }
  
  // Log polygon bounds
  if (points.length >= 3) {
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    
    console.log(`🔷   Polygon bounds: X[${minX.toFixed(1)}, ${maxX.toFixed(1)}], Y[${minY.toFixed(1)}, ${maxY.toFixed(1)}]`);
    console.log(`🔷   Polygon points (${points.length}):`, points.map(p => `(${p.x.toFixed(1)}, ${p.y.toFixed(1)})`).join(', '));
  } else {
    console.warn(`⚠️ Room "${room.name}": Insufficient polygon points (${points.length})`);
  }
  
  return points;
}

/**
 * Checks if a point is inside a polygon using the ray casting algorithm
 */
function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false;
  
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    
    if (((pi.y > point.y) !== (pj.y > point.y)) &&
        (point.x < (pj.x - pi.x) * (point.y - pi.y) / (pj.y - pi.y) + pi.x)) {
      inside = !inside;
    }
  }
  
  return inside;
}

/**
 * Calculates the area of intersection between a rectangle and a polygon
 */
function calculateRectanglePolygonIntersection(
  rectStart: Point,
  rectEnd: Point,
  polygon: Point[],
  roomName?: string
): number {
  if (polygon.length < 3) return 0;
  
  const minX = Math.min(rectStart.x, rectEnd.x);
  const maxX = Math.max(rectStart.x, rectEnd.x);
  const minY = Math.min(rectStart.y, rectEnd.y);
  const maxY = Math.max(rectStart.y, rectEnd.y);
  
  // Create rectangle corners
  const rectCorners = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY }
  ];
  
  // 🎯 ACCURACY FIX: Reduced sample size from 5px to 2px for much better accuracy
  // This is critical for detecting partial room intersections
  const sampleSize = 2; // Sample every 2 pixels for high accuracy
  const rectWidth = maxX - minX;
  const rectHeight = maxY - minY;
  
  const roomLabel = roomName ? ` for room "${roomName}"` : '';
  console.log(`🔍 INTERSECTION SAMPLING${roomLabel}: Rectangle (${minX.toFixed(1)}, ${minY.toFixed(1)}) to (${maxX.toFixed(1)}, ${maxY.toFixed(1)})`);
  console.log(`🔍   Rectangle dimensions: ${rectWidth.toFixed(1)}px × ${rectHeight.toFixed(1)}px`);
  console.log(`🔍   Sample size: ${sampleSize}px, Expected samples: ${Math.ceil(rectWidth / sampleSize)} × ${Math.ceil(rectHeight / sampleSize)}`);
  
  // Check if polygon bounds overlap with rectangle at all
  const polyMinX = Math.min(...polygon.map(p => p.x));
  const polyMaxX = Math.max(...polygon.map(p => p.x));
  const polyMinY = Math.min(...polygon.map(p => p.y));
  const polyMaxY = Math.max(...polygon.map(p => p.y));
  
  const overlapsX = !(polyMaxX < minX || polyMinX > maxX);
  const overlapsY = !(polyMaxY < minY || polyMinY > maxY);
  const boundsOverlap = overlapsX && overlapsY;
  
  console.log(`🔍   Polygon bounds: X[${polyMinX.toFixed(1)}, ${polyMaxX.toFixed(1)}], Y[${polyMinY.toFixed(1)}, ${polyMaxY.toFixed(1)}]`);
  console.log(`🔍   Bounds overlap check: X=${overlapsX}, Y=${overlapsY}, Overall=${boundsOverlap}`);
  
  if (!boundsOverlap) {
    console.log(`⚠️ No bounds overlap - skipping detailed sampling`);
    return 0;
  }
  
  let samplesInside = 0;
  let totalSamples = 0;
  
  for (let x = minX; x <= maxX; x += sampleSize) {
    for (let y = minY; y <= maxY; y += sampleSize) {
      const samplePoint = { x, y };
      totalSamples++;
      
      if (isPointInPolygon(samplePoint, polygon)) {
        samplesInside++;
      }
    }
  }
  
  if (totalSamples === 0) return 0;
  
  // Calculate the intersection area in pixels
  const intersectionRatio = samplesInside / totalSamples;
  const rectangleArea = rectWidth * rectHeight;
  const intersectionAreaPixels = rectangleArea * intersectionRatio;
  
  console.log(`🔍 INTERSECTION RESULT: ${samplesInside}/${totalSamples} samples inside (${(intersectionRatio * 100).toFixed(1)}%)`);
  console.log(`🔍   Rectangle area: ${rectangleArea.toFixed(1)} px²`);
  console.log(`🔍   Intersection area: ${intersectionAreaPixels.toFixed(1)} px²`);
  
  // Convert to square meters and ensure exactly 2 decimal places for area accuracy
  const areaInSquareMeters = pixelsToMeters(Math.sqrt(intersectionAreaPixels)) ** 2;
  console.log(`🔍   Final area: ${areaInSquareMeters.toFixed(2)} m²`);
  
  return Math.round(areaInSquareMeters * 100) / 100;
}

/**
 * Determines if a highlight area is constrained to room boundaries
 */
function isHighlightWithinRoomBoundaries(highlight: HighlightArea, rooms: Room[]): boolean {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);
  
  // Check if all corners of the highlight rectangle are within at least one room
  const corners = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY }
  ];
  
  for (const corner of corners) {
    let cornerInAnyRoom = false;
    
    for (const room of rooms) {
      const polygon = getRoomPolygon(room);
      if (isPointInPolygon(corner, polygon)) {
        cornerInAnyRoom = true;
        break;
      }
    }
    
    if (!cornerInAnyRoom) {
      return false; // At least one corner is outside all rooms
    }
  }
  
  return true;
}

/**
 * Calculates the portion of a wall that intersects with the highlight area
 * This focuses on geometric intersection rather than room membership
 */
function calculateConstrainedWallIntersectionLength(
  wall: Wall | WallWithTempId, 
  highlight: HighlightArea,
  rooms: Room[]
): number {
  const minX = Math.min(highlight.startPoint.x, highlight.endPoint.x);
  const maxX = Math.max(highlight.startPoint.x, highlight.endPoint.x);
  const minY = Math.min(highlight.startPoint.y, highlight.endPoint.y);
  const maxY = Math.max(highlight.startPoint.y, highlight.endPoint.y);

  const wallStart = wall.start;
  const wallEnd = wall.end;

  // 🚨 CORNER DEBUG: Check if this wall shares endpoints with other walls
  const wallLength = Math.hypot(wallEnd.x - wallStart.x, wallEnd.y - wallStart.y);
  console.log(`🔧 🚨 CORNER DEBUG: Processing wall from (${wallStart.x}, ${wallStart.y}) to (${wallEnd.x}, ${wallEnd.y}), length: ${wallLength.toFixed(2)}px, type: ${wall.type || 'undefined'}`);

  // Check if wall endpoints are within the highlight area
  const startInside = (wallStart.x >= minX && wallStart.x <= maxX && 
                      wallStart.y >= minY && wallStart.y <= maxY);
  const endInside = (wallEnd.x >= minX && wallEnd.x <= maxX && 
                    wallEnd.y >= minY && wallEnd.y <= maxY);

  console.log(`🔧 Point inside/outside check:`);
  console.log(`🔧   wallStart (${wallStart.x}, ${wallStart.y}): ${startInside ? 'INSIDE' : 'OUTSIDE'}`);
  console.log(`🔧   wallEnd (${wallEnd.x}, ${wallEnd.y}): ${endInside ? 'INSIDE' : 'OUTSIDE'}`);
  console.log(`🔧   Rectangle: (${minX}, ${minY}) to (${maxX}, ${maxY})`);

  // 🚨 CORNER DEBUG: Check if endpoints are exactly on rectangle boundaries
  const startOnBoundary = (wallStart.x === minX || wallStart.x === maxX || wallStart.y === minY || wallStart.y === maxY);
  const endOnBoundary = (wallEnd.x === minX || wallEnd.x === maxX || wallEnd.y === minY || wallEnd.y === maxY);
  console.log(`🔧 🚨 CORNER DEBUG: startOnBoundary=${startOnBoundary}, endOnBoundary=${endOnBoundary}`);

  let intersectionStart = wallStart;
  let intersectionEnd = wallEnd;

  // Case 1: Both endpoints are inside the highlight area
  if (startInside && endInside) {
    // Use the full wall segment
    const intersectionLength = Math.hypot(wallEnd.x - wallStart.x, wallEnd.y - wallStart.y);
    console.log(`🔧 🚨 CORNER DEBUG: Both endpoints inside - full wall length: ${pixelsToMeters(intersectionLength).toFixed(2)}m`);
    return pixelsToMeters(intersectionLength);
  }

  // Case 2: Both endpoints are outside - check if wall crosses the rectangle
  if (!startInside && !endInside) {
    console.log(`🔧 🚨 CORNER DEBUG: Both endpoints outside - checking if wall crosses rectangle`);
    const intersections = [];
    
    // Define rectangle edges
    const rectEdges = [
      { start: { x: minX, y: minY }, end: { x: maxX, y: minY } }, // top
      { start: { x: maxX, y: minY }, end: { x: maxX, y: maxY } }, // right
      { start: { x: maxX, y: maxY }, end: { x: minX, y: maxY } }, // bottom
      { start: { x: minX, y: maxY }, end: { x: minX, y: minY } }  // left
    ];

    // Find all intersection points
    for (const edge of rectEdges) {
      const intersection = getLineIntersection(wallStart, wallEnd, edge.start, edge.end);
      if (intersection) {
        intersections.push(intersection);
      }
    }

    console.log(`🔧 🚨 CORNER DEBUG: Found ${intersections.length} intersection points:`, intersections);

    if (intersections.length >= 2) {
      // Wall crosses through the rectangle
      intersectionStart = intersections[0];
      intersectionEnd = intersections[1];
      const intersectionLength = Math.hypot(
        intersectionEnd.x - intersectionStart.x,
        intersectionEnd.y - intersectionStart.y
      );
      console.log(`🔧 🚨 CORNER DEBUG: Wall crosses rectangle - intersection length: ${pixelsToMeters(intersectionLength).toFixed(2)}m`);
      return pixelsToMeters(intersectionLength);
    } else {
      // Wall doesn't intersect with the rectangle
      console.log(`🔧 🚨 CORNER DEBUG: Wall doesn't intersect rectangle - returning 0`);
      return 0;
    }
  }

  // Case 3: One endpoint inside, one outside
    const rectEdges = [
      { start: { x: minX, y: minY }, end: { x: maxX, y: minY } }, // top
      { start: { x: maxX, y: minY }, end: { x: maxX, y: maxY } }, // right
      { start: { x: maxX, y: maxY }, end: { x: minX, y: maxY } }, // bottom
      { start: { x: minX, y: maxY }, end: { x: minX, y: minY } }  // left
    ];

  if (startInside && !endInside) {
    // Wall starts inside, ends outside - find exit point
    console.log(`🔧 🚨 CORNER DEBUG: Start inside, end outside - finding exit point`);
    console.log(`🔧 Looking for exit point: wall (${wallStart.x}, ${wallStart.y}) -> (${wallEnd.x}, ${wallEnd.y})`);
    console.log(`🔧 Rectangle bounds: (${minX}, ${minY}) to (${maxX}, ${maxY})`);
    
    // Check if wall is collinear with any rectangle edge
    for (const edge of rectEdges) {
      const isCollinear = areCollinear(wallStart, wallEnd, edge.start, edge.end);
      console.log(`🔧 Testing collinearity with edge (${edge.start.x}, ${edge.start.y}) -> (${edge.end.x}, ${edge.end.y}): ${isCollinear}`);
      if (isCollinear) {
        console.log(`🔧 🚨 CORNER DEBUG: Wall is collinear with edge - finding overlap`);
        // Find the overlap segment - use the full overlap result, not just wallStart
        const fullOverlap = findCollinearOverlap(wallStart, wallEnd, edge.start, edge.end);
        if (fullOverlap) {
          intersectionStart = fullOverlap.start;
          intersectionEnd = fullOverlap.end;
          console.log(`🔧 🚨 CORNER DEBUG: Using full overlap from (${fullOverlap.start.x}, ${fullOverlap.start.y}) to (${fullOverlap.end.x}, ${fullOverlap.end.y})`);
          break;
        }
      }
    }
    
    // If no collinear overlap found, use normal intersection detection
    if (intersectionStart === wallStart && intersectionEnd === wallEnd) {
      console.log(`🔧 🚨 CORNER DEBUG: No collinear overlap - using normal intersection detection`);
      for (const edge of rectEdges) {
        const intersection = getLineIntersection(wallStart, wallEnd, edge.start, edge.end);
        console.log(`🔧 Testing edge (${edge.start.x}, ${edge.start.y}) -> (${edge.end.x}, ${edge.end.y}): intersection = ${intersection ? `(${intersection.x}, ${intersection.y})` : 'null'}`);
        if (intersection) {
          // Check if intersection is not at wallStart (avoid zero-length segments)
          const distanceToIntersection = Math.hypot(intersection.x - wallStart.x, intersection.y - wallStart.y);
          console.log(`🔧 🚨 CORNER DEBUG: Distance to intersection: ${distanceToIntersection.toFixed(2)}px`);
          if (distanceToIntersection > 0.1) { // minimum threshold to avoid floating point errors
            intersectionStart = wallStart;
            intersectionEnd = intersection;
            console.log(`🔧 🚨 CORNER DEBUG: Found valid exit intersection at (${intersection.x}, ${intersection.y})`);
            break;
          } else {
            console.log(`🔧 🚨 CORNER DEBUG: Intersection too close to wall start - skipping`);
          }
        }
      }
    }
  } else if (!startInside && endInside) {
    // Wall starts outside, ends inside - find entry point
    console.log(`🔧 🚨 CORNER DEBUG: Start outside, end inside - finding entry point`);
    console.log(`🔧 Looking for entry point: wall (${wallStart.x}, ${wallStart.y}) -> (${wallEnd.x}, ${wallEnd.y})`);
    console.log(`🔧 Rectangle bounds: (${minX}, ${minY}) to (${maxX}, ${maxY})`);
    
    // Check if wall is collinear with any rectangle edge
    for (const edge of rectEdges) {
      const isCollinear = areCollinear(wallStart, wallEnd, edge.start, edge.end);
      console.log(`🔧 Testing collinearity with edge (${edge.start.x}, ${edge.start.y}) -> (${edge.end.x}, ${edge.end.y}): ${isCollinear}`);
      if (isCollinear) {
        console.log(`🔧 🚨 CORNER DEBUG: Wall is collinear with edge - finding overlap`);
        // Find the overlap segment - use the full overlap result, not just wallEnd
        const fullOverlap = findCollinearOverlap(wallStart, wallEnd, edge.start, edge.end);
        if (fullOverlap) {
          intersectionStart = fullOverlap.start;
          intersectionEnd = fullOverlap.end;
          console.log(`🔧 🚨 CORNER DEBUG: Using full overlap from (${fullOverlap.start.x}, ${fullOverlap.start.y}) to (${fullOverlap.end.x}, ${fullOverlap.end.y})`);
          break;
        }
      }
    }
    
    // If no collinear overlap found, use normal intersection detection
    if (intersectionStart === wallStart && intersectionEnd === wallEnd) {
      console.log(`🔧 🚨 CORNER DEBUG: No collinear overlap - using normal intersection detection`);
      for (const edge of rectEdges) {
        const intersection = getLineIntersection(wallStart, wallEnd, edge.start, edge.end);
        console.log(`🔧 Testing edge (${edge.start.x}, ${edge.start.y}) -> (${edge.end.x}, ${edge.end.y}): intersection = ${intersection ? `(${intersection.x}, ${intersection.y})` : 'null'}`);
        if (intersection) {
          // Check if intersection is not at wallEnd (avoid zero-length segments)
          const distanceToIntersection = Math.hypot(intersection.x - wallEnd.x, intersection.y - wallEnd.y);
          console.log(`🔧 🚨 CORNER DEBUG: Distance to intersection: ${distanceToIntersection.toFixed(2)}px`);
          if (distanceToIntersection > 0.1) { // minimum threshold to avoid floating point errors
            intersectionStart = intersection;
            intersectionEnd = wallEnd;
            console.log(`🔧 🚨 CORNER DEBUG: Found valid entry intersection at (${intersection.x}, ${intersection.y})`);
            break;
          } else {
            console.log(`🔧 🚨 CORNER DEBUG: Intersection too close to wall end - skipping`);
          }
        }
      }
    }
  }

  // Calculate the length of the intersection segment
  const intersectionLength = Math.hypot(
    intersectionEnd.x - intersectionStart.x,
    intersectionEnd.y - intersectionStart.y
  );

  const result = pixelsToMeters(intersectionLength);
  console.log(`🔧 🚨 CORNER DEBUG: Final intersection calculation:`);
  console.log(`🔧   intersectionStart: (${intersectionStart.x}, ${intersectionStart.y})`);
  console.log(`🔧   intersectionEnd: (${intersectionEnd.x}, ${intersectionEnd.y})`);
  console.log(`🔧   Distance in pixels: ${intersectionLength.toFixed(2)}`);
  console.log(`🔧   Distance in meters: ${result.toFixed(2)}m`);
  console.log(`🔧 🚨 CORNER DEBUG: Returning ${result.toFixed(2)}m for wall type ${wall.type || 'undefined'}`);
  return result;
}

/**
 * Helper function to find intersection point between two line segments
 */
function getLineIntersection(
  p1: Point, p2: Point, // First line segment
  p3: Point, p4: Point  // Second line segment
): Point | null {
  const x1 = p1.x, y1 = p1.y;
  const x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y;
  const x4 = p4.x, y4 = p4.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) {
    return null; // Lines are parallel
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1)
    };
  }

  return null; // Lines don't intersect within the segments
}

/**
 * Helper function to check if two line segments are collinear
 */
function areCollinear(p1: Point, q1: Point, p2: Point, q2: Point): boolean {
  // Calculate cross product to check if lines are parallel
  const crossProduct = (q1.x - p1.x) * (q2.y - p2.y) - (q1.y - p1.y) * (q2.x - p2.x);
  if (Math.abs(crossProduct) > 1e-10) {
    return false; // Lines are not parallel
  }
  
  // Check if point p2 lies on the line defined by p1 and q1
  const crossProductPoint = (p2.x - p1.x) * (q1.y - p1.y) - (p2.y - p1.y) * (q1.x - p1.x);
  return Math.abs(crossProductPoint) < 1e-10; // Lines are collinear if p2 lies on line p1-q1
}

/**
 * Helper function to find the overlap between two collinear segments
 */
function findCollinearOverlap(p1: Point, q1: Point, p2: Point, q2: Point): { start: Point, end: Point } | null {
  // Ensure segments are collinear
  if (!areCollinear(p1, q1, p2, q2)) {
    return null;
  }
  
  console.log(`🔧 🚨 OVERLAP DEBUG: Finding overlap between segments:`);
  console.log(`🔧   Segment 1: (${p1.x}, ${p1.y}) -> (${q1.x}, ${q1.y})`);
  console.log(`🔧   Segment 2: (${p2.x}, ${p2.y}) -> (${q2.x}, ${q2.y})`);
  
  // For horizontal segments (same y-coordinate)
  if (Math.abs(p1.y - q1.y) < 1e-10 && Math.abs(p2.y - q2.y) < 1e-10) {
    const x1Min = Math.min(p1.x, q1.x);
    const x1Max = Math.max(p1.x, q1.x);
    const x2Min = Math.min(p2.x, q2.x);
    const x2Max = Math.max(p2.x, q2.x);
    
    console.log(`🔧 🚨 OVERLAP DEBUG: Horizontal segments:`);
    console.log(`🔧   Segment 1 X range: ${x1Min} to ${x1Max}`);
    console.log(`🔧   Segment 2 X range: ${x2Min} to ${x2Max}`);
    
    const overlapStart = Math.max(x1Min, x2Min);
    const overlapEnd = Math.min(x1Max, x2Max);
    
    console.log(`🔧   Calculated overlap: X from ${overlapStart} to ${overlapEnd}`);
    
    if (overlapStart <= overlapEnd) {
      const result = {
        start: { x: overlapStart, y: p1.y },
        end: { x: overlapEnd, y: p1.y }
      };
      console.log(`🔧 🚨 OVERLAP DEBUG: Returning overlap: (${result.start.x}, ${result.start.y}) -> (${result.end.x}, ${result.end.y})`);
      return result;
    } else {
      console.log(`🔧 🚨 OVERLAP DEBUG: No overlap - overlapStart (${overlapStart}) > overlapEnd (${overlapEnd})`);
    }
  }
  
  // For vertical segments (same x-coordinate)
  if (Math.abs(p1.x - q1.x) < 1e-10 && Math.abs(p2.x - q2.x) < 1e-10) {
    const y1Min = Math.min(p1.y, q1.y);
    const y1Max = Math.max(p1.y, q1.y);
    const y2Min = Math.min(p2.y, q2.y);
    const y2Max = Math.max(p2.y, q2.y);
    
    console.log(`🔧 🚨 OVERLAP DEBUG: Vertical segments:`);
    console.log(`🔧   Segment 1 Y range: ${y1Min} to ${y1Max}`);
    console.log(`🔧   Segment 2 Y range: ${y2Min} to ${y2Max}`);
    
    const overlapStart = Math.max(y1Min, y2Min);
    const overlapEnd = Math.min(y1Max, y2Max);
    
    console.log(`🔧   Calculated overlap: Y from ${overlapStart} to ${overlapEnd}`);
    
    if (overlapStart <= overlapEnd) {
      const result = {
        start: { x: p1.x, y: overlapStart },
        end: { x: p1.x, y: overlapEnd }
      };
      console.log(`🔧 🚨 OVERLAP DEBUG: Returning overlap: (${result.start.x}, ${result.start.y}) -> (${result.end.x}, ${result.end.y})`);
      return result;
    } else {
      console.log(`🔧 🚨 OVERLAP DEBUG: No overlap - overlapStart (${overlapStart}) > overlapEnd (${overlapEnd})`);
    }
  }
  
  console.log(`🔧 🚨 OVERLAP DEBUG: Segments are not horizontal or vertical - returning null`);
  return null;
}

/**
 * Helper function to find the end point of the overlap segment
 */
function findSegmentOverlapEnd(p1: Point, q1: Point, p2: Point, q2: Point): Point | null {
  const overlap = findCollinearOverlap(p1, q1, p2, q2);
  return overlap ? overlap.end : null;
}

/**
 * Helper function to find the start point of the overlap segment
 */
function findSegmentOverlapStart(p1: Point, q1: Point, p2: Point, q2: Point): Point | null {
  const overlap = findCollinearOverlap(p1, q1, p2, q2);
  return overlap ? overlap.start : null;
}

/**
 * Calculates area metrics for a highlight area based on intersection with room boundaries
 * Uses geometric intersection to calculate only the highlighted portion of rooms
 */
export function calculateHighlightMetrics(
  highlight: HighlightArea,
  walls: (Wall | WallWithTempId)[],
  rooms: Room[] = []
): {
  area: number;
  hlp: number;
  pw: number;
  sw: number;
} {
  try {
    console.log(`🚀 CALCULATE_HIGHLIGHT_METRICS CALLED: walls=${walls.length}, rooms=${rooms.length}`);
    console.log(`🚀 Highlight coordinates: start=(${highlight.startPoint.x}, ${highlight.startPoint.y}), end=(${highlight.endPoint.x}, ${highlight.endPoint.y})`);
    
    console.log(`🎯 Enhanced highlight area calculation: Analyzing ${rooms.length} rooms for intersection`);
    
    // Check if highlight is constrained to room boundaries
    const isWithinRoomBoundaries = isHighlightWithinRoomBoundaries(highlight, rooms);
    
    // Calculate intersection area with each room
    let totalIntersectionArea = 0;
    const roomIntersections: Array<{
      room: Room;
      intersectionArea: number;
      percentage: number;
    }> = [];
    
    for (const room of rooms) {
      try {
        console.log(`\n📐 Processing room "${room.name}" (${room.area.toFixed(2)} m²)`);
        const roomPolygon = getRoomPolygon(room);
        if (roomPolygon.length < 3) {
          console.log(`⚠️ Room "${room.name}" has insufficient polygon points (${roomPolygon.length})`);
          continue;
        }
        
        const intersectionArea = calculateRectanglePolygonIntersection(
          highlight.startPoint,
          highlight.endPoint,
          roomPolygon,
          room.name
        );
        
        if (intersectionArea > 0) {
          const percentage = (intersectionArea / room.area) * 100;
          totalIntersectionArea += intersectionArea;
          
          roomIntersections.push({
            room,
            intersectionArea,
            percentage
          });
          
          console.log(`🎯 Room "${room.name}" intersection:`, {
            intersectionArea: intersectionArea.toFixed(2) + ' m²',
            fullRoomArea: room.area.toFixed(2) + ' m²',
            percentage: percentage.toFixed(1) + '%'
          });
        }
      } catch (roomError) {
        console.error(`❌ Error processing room "${room.name}":`, roomError);
      }
    }
  
    console.log(`🎯 Total intersection area: ${totalIntersectionArea.toFixed(2)} m² from ${roomIntersections.length} rooms`);
  
    // Calculate area - use intersection area if available, otherwise fallback to rectangle area
    let finalArea = totalIntersectionArea;
    if (totalIntersectionArea === 0) {
      console.log('⚠️ No room intersections found, using fallback rectangle area');
      finalArea = calculateHighlightArea(highlight);
    } else if (!isWithinRoomBoundaries) {
      console.log('⚠️ Highlight extends outside room boundaries, but found room intersections');
    }
  
    // Find walls that intersect with the highlight area
    const intersectingWalls: (Wall | WallWithTempId)[] = [];
    
    // Get all walls from rooms that intersect with the highlight
    for (const roomIntersection of roomIntersections) {
      for (const wall of roomIntersection.room.walls) {
        // Check if this wall intersects with the highlight area
        if (doesWallIntersectHighlight(wall, highlight)) {
          intersectingWalls.push(wall);
        }
      }
    }
    
    // Also include standalone walls that intersect with the highlight
    for (const wall of walls) {
      if (doesWallIntersectHighlight(wall, highlight)) {
        // Check if this wall isn't already included from room walls
        const wallExists = intersectingWalls.some(w => 
          Math.abs(w.start.x - wall.start.x) < 1 &&
          Math.abs(w.start.y - wall.start.y) < 1 &&
          Math.abs(w.end.x - wall.end.x) < 1 &&
          Math.abs(w.end.y - wall.end.y) < 1
        );
        
        if (!wallExists) {
          intersectingWalls.push(wall);
        }
      }
    }
    
    console.log(`🔧 Found ${intersectingWalls.length} walls intersecting with highlight area`);
    
    // 🔍 DEBUG: Show exact wall section types for each wall
    intersectingWalls.forEach((wall, index) => {
      const wallDesc = `Wall ${index}: (${wall.start.x.toFixed(1)}, ${wall.start.y.toFixed(1)}) -> (${wall.end.x.toFixed(1)}, ${wall.end.y.toFixed(1)})`;
      if ((wall as any).sections && (wall as any).sections.length > 0) {
        const sectionTypes = (wall as any).sections.map((s: any, i: number) => 
          `Section ${i}: type='${s.type}', isExterior=${s.isExterior}`
        ).join(', ');
        console.log(`🔍 ${wallDesc} - ${sectionTypes}`);
      } else {
        console.log(`🔍 ${wallDesc} - No sections, main type='${wall.type}'`);
      }
    });
    
    // 🔧 AUTO-CORRECT: Fix obviously wrong section types
    // Interior walls (isExterior=false) should be PW (party wall), not HLP
    intersectingWalls.forEach((wall) => {
      if ((wall as any).sections && (wall as any).sections.length > 0) {
        (wall as any).sections.forEach((section: any) => {
          // If section is interior (isExterior=false) but type is HLP or SW, change to PW
          if (!section.isExterior && (section.type === 'HLP' || section.type === 'SW')) {
            console.log(`🔧 AUTO-CORRECT: Interior wall had type='${section.type}', changing to 'PW'`);
            section.type = 'PW';
          }
          // If section is None type, infer from isExterior
          if (section.type === 'None' || !section.type) {
            const inferredType = section.isExterior ? 'HLP' : 'PW';
            console.log(`🔧 AUTO-INFER: Section type was '${section.type}', setting to '${inferredType}' based on isExterior=${section.isExterior}`);
            section.type = inferredType;
          }
        });
      }
    });
    
    // Calculate wall lengths by type using the intersection calculation
    let hlpLength = 0;
    let pwLength = 0;
    let swLength = 0;
    
    // Debug: Log all wall types found
    const wallTypesSummary = intersectingWalls.reduce((acc, wall) => {
      const wallType = wall.type || 'undefined';
      acc[wallType] = (acc[wallType] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    console.log(`🔧 Wall types found in highlight area:`, wallTypesSummary);
    
    for (const wall of intersectingWalls) {
      try {
        const wallLength = calculateConstrainedWallIntersectionLength(wall, highlight, roomIntersections.map(r => r.room));
        
        console.log(`🔧 Processing wall: (${wall.start.x}, ${wall.start.y}) -> (${wall.end.x}, ${wall.end.y}), type: ${wall.type}, length: ${wallLength.toFixed(2)}m`);
        
        if (wallLength > 0) {
          // 🆕 ENHANCED SECTION-BASED CALCULATION: Handle wall sections in highlighter
          if ((wall as any).sections && (wall as any).sections.length > 0) {
            console.log(`🔧 Wall has ${(wall as any).sections.length} sections - calculating section-based lengths`);
            
            const totalWallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
            const totalWallMeterLength = pixelsToMeters(totalWallPixelLength);
            
            // Calculate what fraction of the wall intersects with the highlight
            const intersectionFraction = wallLength / totalWallMeterLength;
            
            (wall as any).sections.forEach((section: any, index: number) => {
              const sectionPixelLength = totalWallPixelLength * (section.endPercent - section.startPercent);
              const sectionMeterLength = pixelsToMeters(sectionPixelLength);
              
              // The intersected length for this section is proportional to the overall intersection
              const sectionIntersectionLength = sectionMeterLength * intersectionFraction;
              
              console.log(`🔧 Section ${index} (${section.type}): ${sectionIntersectionLength.toFixed(2)}m of ${sectionMeterLength.toFixed(2)}m total`);
              
              if (sectionIntersectionLength > 0) {
                switch (section.type) {
                  case 'HLP':
                    hlpLength += sectionIntersectionLength;
                    break;
                  case 'PW':
                    pwLength += sectionIntersectionLength;
                    break;
                  case 'SW':
                    swLength += sectionIntersectionLength;
                    break;
                  case 'None':
                  default:
                    console.log(`🔧 Section with type '${section.type}' (${sectionIntersectionLength.toFixed(2)}m) - not counted in HLP/PW/SW`);
                    break;
                }
              }
            });
          } else {
            // Standard calculation for walls without sections
            const wallType = wall.type || 'None';
            console.log(`🔧 Wall type ${wallType}: ${wallLength.toFixed(2)}m`);
            
            switch (wallType) {
              case 'HLP':
                hlpLength += wallLength;
                break;
              case 'PW':
                pwLength += wallLength;
                break;
              case 'SW':
                swLength += wallLength;
                break;
              case 'None':
              default:
                // For walls without specific type, don't count them in any category
                console.log(`🔧 Wall with type '${wallType}' (${wallLength.toFixed(2)}m) - not counted in HLP/PW/SW`);
                break;
            }
          }
        }
      } catch (wallError) {
        console.error(`❌ Error processing wall from (${wall.start.x}, ${wall.start.y}) to (${wall.end.x}, ${wall.end.y}):`, wallError);
      }
    }
    
    // HLP calculation includes both HLP and SW walls (as per the existing logic)
    const totalHlp = hlpLength + swLength;
    
    console.log(`🔧 Final wall calculations:`, {
      'HLP length': hlpLength.toFixed(2) + 'm',
      'PW length': pwLength.toFixed(2) + 'm', 
      'SW length': swLength.toFixed(2) + 'm',
      'Total HLP (HLP + SW)': totalHlp.toFixed(2) + 'm'
    });
    
    return {
      area: Math.round(finalArea * 100) / 100,
      hlp: Math.round(totalHlp * 100) / 100,
      pw: Math.round(pwLength * 100) / 100,
      sw: Math.round(swLength * 100) / 100
    };
  } catch (error) {
    console.error('❌ An unexpected error occurred during highlight metrics calculation:', error);
    return { area: 0, hlp: 0, pw: 0, sw: 0 };
  }
}

// Export helper functions for use in other components
export { doesWallIntersectHighlight, calculateConstrainedWallIntersectionLength };